import * as Sentry from '@sentry/nextjs';
import CTA from 'components/Campaign/CTA';
import { Petition } from 'components/cards';
import Layout, { DATA_TYPES } from 'components/layout';
import ShareBlock from 'components/Sharing';

import Anchor from 'components/typography/anchor';
import { getLetters, getPetitionTrend } from 'lib/api/themis/queries/letters';
import { otelApi } from 'lib/instrumentation'; // Initializes instrumentation
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import config, {
  DATA_TYPES as CONTENT_TYPES,
  LETTER_STATUSES,
} from 'siteconfig';
import slugify from 'slugify';
import { getOpenGraphUrl } from 'utilities/paths';
import { CampaignTypeList, OpenLetterType } from 'utilities/propTypes';
import { getStateAPAbbreviation } from 'utilities/states';
import {
  buildLetterTitle,
  formatLetterFields,
  normalizeShoutingSubject,
} from 'utilities/textPresentation';

const REVALIDATE_TIME_MINUTES = 10;
const REVALIDATE_TIME_SECONDS_FIVE_MINUTES = REVALIDATE_TIME_MINUTES * 60;
const TREND_DATA_DAYS_WINDOW = 7;

const currentYear = new Date().getFullYear();

export const getStaticProps = Sentry.wrapGetStaticPropsWithSentry(
  async ({ params }) => {
    const span = otelApi.trace.getActiveSpan();

    const correlationId = params.correlationid.toUpperCase();
    const letters = await getLetters({ correlationId });
    // They're all the same, we can use one for visual purposes
    const letter = letters[0];

    const isPrivate = letters.reduce((prev, curr) => {
      return prev || curr.status !== LETTER_STATUSES.PUBLIC;
    }, false);

    if (!letter || isPrivate) {
      return {
        notFound: true,
        revalidate: REVALIDATE_TIME_SECONDS_FIVE_MINUTES,
      };
    }

    // Parallel this with the user lookup (which will be merged into the letter lookup)
    let trending = null;
    if (letter?.petition?.shareCode) {
      trending = await getPetitionTrend({
        shareCode: letter.petition.shareCode,
        days: TREND_DATA_DAYS_WINDOW,
      });
    }

    const formattedLetter = {
      messageId: letter.id,
      correlationId: letter.correlationId,
      status: letter.status || LETTER_STATUSES.PRIVATE,
      subject: letter.content.subject,
      message: letter.content.message,
      sender: {
        city: letter.sender.city,
        state: letter.sender.state,
      },
      verifiedVoter: letter.sender.verifiedVoter || false,
      sentTimestamp: letter.sentTimestamp,
      shareCode: letter?.petition?.shareCode || '',
      target: {
        fullName: letter.legislator.fullName,
        city: letter?.petition?.city || '',
        state: letter?.petition?.state || '',
        bioguideId: letter.legislator.id,
      },
      district: {
        city: letter.district.city,
        state: letter.district.state,
        county: letter.district.county,
      },
    };

    const petition = {
      city: letter?.petition?.city || '',
      state: letter?.petition?.state || '',
      petition_code: letter?.petition?.shareCode || '',
      sign_count: letter?.petition?.totalSigns || 0,
      trending: {
        sign_count_period: trending?.signCount || 0,
      },
      subject: letter?.petition?.subject || '',
      target: letter?.petition?.targetType || '',
    };

    const targets = letters.map((letter) => {
      return {
        fullName: letter.legislator.fullName,
        bioguideId: letter.legislator.id,
        link: `/explore/person/${encodeURIComponent(
          letter.legislator.id
        )}/${slugify(letter.legislator.fullName)}`,
        humanName: `${letter.legislator.titleAbbreviation} ${letter.legislator.lastName}`,
      };
    });

    // Find a state code in any of the letters, not optimal
    const stateCode = letters.reduce((prev, curr) => {
      return prev || curr.district.state;
    }, null);

    span.setAttributes({
      'state.code': stateCode,
      'letter.correlationId': correlationId,
      'letter.targets': targets.map((t) => t.bioguideId).join(', '),
      'letter.petition': petition?.petition_code || '',
    });
    return {
      props: {
        letter: formattedLetter,
        petition: petition,
        targets: targets,
        stateCode: stateCode,
      },
      // Doesn't update often
      revalidate: REVALIDATE_TIME_SECONDS_FIVE_MINUTES,
    };
  }
);

// Don't pre-render anything
export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

const LetterMetadata = ({
  dateString,
  verifiedVoter,
  targets,
  subject,
  city,
  state,
}) => {
  const name = verifiedVoter ? 'verified voter' : 'constituent';

  return (
    <>
      <h1 className="mb-4">{subject ? subject : 'An Open Letter'}</h1>
      <p>
        <strong>To:</strong>{' '}
        {targets
          .map((t) => (
            <Anchor className="anchor-text" href={t.link} key={t.bioguideId}>
              {t.humanName}
            </Anchor>
          ))
          .reduce((acc, curr, index) => {
            if (index !== 0) {
              acc.push(', ');
            }
            acc.push(curr);
            return acc;
          }, [])}
      </p>
      <p>
        <strong>From:</strong> A {name} in {city}, {state}
      </p>
      <p>{dateString}</p>
    </>
  );
};

const Letter = ({ letter, petition, targets, stateCode }) => {
  const router = useRouter();
  const currentUrl = new URL(router.asPath, config.siteUrl).href;

  const targetName = letter.target.fullName;

  // JS uses millis based timestamp, but the API returns seconds
  const date = new Date(letter.sentTimestamp * 1000);
  const isThisYear = date.getFullYear() === currentYear;
  const dateString = isThisYear
    ? // October 14
      `${new Intl.DateTimeFormat('en-US', { month: 'long' }).format(
        date
      )} ${date.getDate()}`
    : // October 14, 1983
      `${new Intl.DateTimeFormat('en-US', { month: 'long' }).format(
        date
      )} ${date.getDate()}, ${date.getFullYear()}`;
  // Keep the h1 consistent with the cased <title> — when they diverge, Google
  // may rewrite the search title from the h1, reintroducing the shouting
  const subject = normalizeShoutingSubject(letter.subject);

  const joinedRepNames = targets.map((t) => t.humanName).join(', ');
  const formattedTitleAndSub = formatLetterFields({
    subject: letter.subject || '',
    message: letter.message || '',
    reps: joinedRepNames || '',
  });
  const seoTitle = buildLetterTitle({
    subject: letter.subject || '',
    recipient: targets[0]?.humanName || '',
  });

  // Dynamic OG image
  const dynamicOpenGraphUrlHref = getOpenGraphUrl('/api/og/letter/letter', {
    title: formattedTitleAndSub.description,
    fullName: joinedRepNames,
  });

  const imageAltText = `A shareable card that reads, "an open letter to ${joinedRepNames}, ${formattedTitleAndSub.description}" followed by "text RESIST to 50409"`;

  return (
    <Layout
      title={`${seoTitle} | Resistbot`}
      ogTitle={formattedTitleAndSub.title}
      dataType={DATA_TYPES.letter}
      description={formattedTitleAndSub.description}
      dynamicImage={dynamicOpenGraphUrlHref}
      imageWidth={1200}
      imageHeight={630}
      imageAltText={imageAltText}
      canonicalPath={`/${CONTENT_TYPES.LETTER.basePath}/${letter.correlationId}`}
    >
      <div className="flex flex-row flex-wrap gap-9">
        <div className="flex grow basis-80 flex-col gap-4">
          <ol
            aria-label="breadcrumbs"
            className="flex gap-2 text-sm tracking-wider uppercase"
          >
            <li className="after:content-['→']">
              <Anchor
                className="mr-2 anchor-subtle"
                href={`/${CONTENT_TYPES.LETTER.basePath}`}
              >
                United States
              </Anchor>
            </li>
            {/* Mitigation for letters to the President */}
            {/* Eventually fall back to some logic to put the correct state code here */}
            {stateCode ? (
              <li className="after:content-['→']">
                <Anchor
                  className="mr-2 anchor-subtle"
                  href={`/${CONTENT_TYPES.LETTER.basePath}/explore/${stateCode}`}
                >
                  {getStateAPAbbreviation(stateCode)}
                </Anchor>
              </li>
            ) : null}
            <li aria-current="page">Letter</li>
          </ol>

          <section className="border-b border-stone-200 pb-4 dark:border-stone-700">
            <LetterMetadata
              dateString={dateString}
              verifiedVoter={letter.verifiedVoter}
              targets={targets}
              city={letter.sender.city}
              state={letter.sender.state}
              subject={subject}
            />
          </section>

          <p className="mb-7 max-w-prose font-serif leading-relaxed text-pretty break-words whitespace-pre-line font-optical-expanded print:mb-0 print:line-clamp-20 print:max-w-full print:leading-5">
            {letter.message}
          </p>

          <ShareBlock pathname={currentUrl} text={letter.subject} />
        </div>

        <div className="flex-1">
          {petition?.petition_code ? (
            <>
              <h3 className="mb-4">Part of a Campaign</h3>
              <Petition
                key={petition.petition_code}
                petition={petition}
                intervalLabel={`${TREND_DATA_DAYS_WINDOW}d`}
              />
            </>
          ) : (
            <>
              <h2 className="mb-4 text-lg leading-snug">
                Write to {targetName} or any of your elected officials
              </h2>
              <CTA copy={`Send your own letter`} keyword={'write'} />

              <p className="mt-4 text-sm">
                <Anchor className="anchor-subtle" href="/">
                  Resistbot
                </Anchor>{' '}
                is a chatbot that delivers your texts to your elected officials
                by email, fax, or postal mail. Tap above to give it a try or{' '}
                <Anchor className="anchor-subtle" href="/">
                  learn more&nbsp;here
                </Anchor>
                !
              </p>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

Letter.propTypes = {
  letter: OpenLetterType,
  petition: CampaignTypeList,
  targets: PropTypes.arrayOf(
    PropTypes.shape({
      fullName: PropTypes.string,
      bioguideId: PropTypes.string,
      link: PropTypes.string,
      humanName: PropTypes.string,
    })
  ),
  stateCode: PropTypes.string,
};

export default Letter;
